import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SEO } from "@/components/SEO";

export default function RegisterPage() {
  const { sessionId } = useSession();
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "",
    mobile_number: "", whatsapp_number: "", whatsapp_same_as_mobile: true,
    terms_accepted: false, marketing_consent: false,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.terms_accepted) {
      setError("You must accept the Terms to continue");
      return;
    }
    setLoading(true);
    try {
      const whatsapp_number = form.whatsapp_same_as_mobile ? form.mobile_number : form.whatsapp_number;
      await api.post("/auth/register", { ...form, whatsapp_number, anonymous_session_id: sessionId });
      await refreshMe();
      navigate("/business-profile");
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="register-page">
      <SEO title="Create Your Free A8A Account" description="Sign up free to save your business diagnostic scan and unlock deeper cash and collections analysis." noindex />
      <form onSubmit={handleSubmit} className="w-full max-w-md space-y-5 border border-border p-8 bg-card">
        <h1 className="font-heading text-2xl font-medium text-foreground">Create your free account</h1>
        <p className="text-sm text-muted-foreground">Save your results and continue to the deep diagnostic.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="first_name">First name</Label>
            <Input id="first_name" required value={form.first_name} onChange={update("first_name")} data-testid="register-first-name-input" />
          </div>
          <div>
            <Label htmlFor="last_name">Last name</Label>
            <Input id="last_name" required value={form.last_name} onChange={update("last_name")} data-testid="register-last-name-input" />
          </div>
        </div>
        <div>
          <Label htmlFor="email">Work email</Label>
          <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="register-email-input" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" minLength={8} required value={form.password} onChange={update("password")} data-testid="register-password-input" />
        </div>
        <div>
          <Label htmlFor="mobile_number">Mobile number</Label>
          <Input id="mobile_number" type="tel" placeholder="+1 234 567 8900" required
            value={form.mobile_number} onChange={update("mobile_number")} data-testid="register-mobile-number-input" />
          <p className="text-xs text-muted-foreground mt-1">Include your country code (e.g. +1, +44, +91).</p>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox id="whatsapp_same" checked={form.whatsapp_same_as_mobile}
            onCheckedChange={(v) => setForm((f) => ({ ...f, whatsapp_same_as_mobile: !!v }))} data-testid="register-whatsapp-same-checkbox" />
          <Label htmlFor="whatsapp_same" className="text-sm font-normal leading-snug">My WhatsApp number is the same as my mobile number</Label>
        </div>
        {!form.whatsapp_same_as_mobile && (
          <div>
            <Label htmlFor="whatsapp_number">WhatsApp number</Label>
            <Input id="whatsapp_number" type="tel" placeholder="+1 234 567 8900" required
              value={form.whatsapp_number} onChange={update("whatsapp_number")} data-testid="register-whatsapp-number-input" />
          </div>
        )}
        <div className="flex items-start gap-2">
          <Checkbox id="terms" checked={form.terms_accepted}
            onCheckedChange={(v) => setForm((f) => ({ ...f, terms_accepted: !!v }))} data-testid="register-terms-checkbox" />
          <Label htmlFor="terms" className="text-sm font-normal leading-snug">
            I accept the{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-accent underline">Terms of Service</a>
            {" "}and{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-accent underline">Privacy Policy</a>
          </Label>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox id="marketing" checked={form.marketing_consent}
            onCheckedChange={(v) => setForm((f) => ({ ...f, marketing_consent: !!v }))} data-testid="register-marketing-checkbox" />
          <Label htmlFor="marketing" className="text-sm font-normal leading-snug">Send me occasional product updates (optional)</Label>
        </div>
        {error && <p className="text-sm text-destructive" data-testid="register-error-message">{error}</p>}
        <Button type="submit" className="w-full h-11" disabled={loading} data-testid="register-submit-button">
          {loading ? "Creating account…" : "Create Account"}
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          Already have an account? <Link to="/login" className="text-accent underline">Log in</Link>
        </p>
      </form>
    </div>
  );
}
